import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { buildTreeLayout, buildGroupColorMap } from '../../utils/treeLayout';
import GenerationColumn from './GenerationColumn';
import ContextMenu from './ContextMenu';

  const FamilyTree = React.forwardRef(({ onMemberClick, onAddSpouse, onTabChange, onAddInColumn }, ref) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('paternal');
  const [contextMenu, setContextMenu] = useState(null);
  
  const fetchMembers = async () => {
    try {
      const response = await axios.get(
        `https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(user.email)}`
      );
      setMembers(response.data.members);
    } catch (err) {
      console.error('Error loading members:', err);
    }
    setLoading(false);
  };

useEffect(() => {
  if (user?.email) fetchMembers();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [user]);

  // Expose refresh() to parent (Dashboard) via ref
  React.useImperativeHandle(ref, () => ({
    refresh: fetchMembers,
  }));

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [contextMenu]);

  const handleRightClick = (e, member) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, member });
  };

  const handleContextAction = async (action, member, selectedIds = [], previousIds = []) => {
    setContextMenu(null);

    if (action === 'delete') {
      const confirmed = window.confirm(
        `Delete "${member.name}" from your family tree? This cannot be undone.`
      );
      if (confirmed) {
        await axios.delete(
          `https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(user.email)}/${member.memberId}`
        );
        fetchMembers();
      }
    }

    if (action === 'deceased') {
      await axios.put(
        `https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(user.email)}/${member.memberId}`,
        { isDeceased: !member.isDeceased }
      );
      fetchMembers();
    }

    if (action === 'add-spouse') {
      onAddSpouse && onAddSpouse(member);
    }

if (action === 'link-parents') {
      const API = 'https://familybandhan-backend-933326390640.asia-south1.run.app';
      const encoded = encodeURIComponent(user.email);
      const newParentIds = selectedIds;
      const oldParentIds = previousIds ?? [];
      const toAdd = newParentIds.filter(id => !oldParentIds.includes(id));
      const toRemove = oldParentIds.filter(id => !newParentIds.includes(id));

      await axios.put(`${API}/api/members/${encoded}/${member.memberId}`, { parentIds: newParentIds });

      for (const parentId of toAdd) {
        const parentMember = members.find(m => m.memberId === parentId);
        const currentChildIds = parentMember?.childIds || [];
        await axios.put(`${API}/api/members/${encoded}/${parentId}`, {
          childIds: [...new Set([...currentChildIds, member.memberId])],
        });
      }
      for (const parentId of toRemove) {
        const parentMember = members.find(m => m.memberId === parentId);
        const currentChildIds = parentMember?.childIds || [];
        await axios.put(`${API}/api/members/${encoded}/${parentId}`, {
          childIds: currentChildIds.filter(id => id !== member.memberId),
        });
      }
      fetchMembers();
    }

    if (action === 'link-children') {
      const API = 'https://familybandhan-backend-933326390640.asia-south1.run.app';
      const encoded = encodeURIComponent(user.email);
      const newChildIds = selectedIds;
      const oldChildIds = previousIds ?? [];
      const toAdd = newChildIds.filter(id => !oldChildIds.includes(id));
      const toRemove = oldChildIds.filter(id => !newChildIds.includes(id));

      await axios.put(`${API}/api/members/${encoded}/${member.memberId}`, { childIds: newChildIds });

      for (const childId of toAdd) {
        const childMember = members.find(m => m.memberId === childId);
        const currentParentIds = childMember?.parentIds || [];
        await axios.put(`${API}/api/members/${encoded}/${childId}`, {
          parentIds: [...new Set([...currentParentIds, member.memberId])],
        });
      }
      for (const childId of toRemove) {
        const childMember = members.find(m => m.memberId === childId);
        const currentParentIds = childMember?.parentIds || [];
        await axios.put(`${API}/api/members/${encoded}/${childId}`, {
          parentIds: currentParentIds.filter(id => id !== member.memberId),
        });
      }
      fetchMembers();
    }
    };

// Only "Self" (the account creator) and their spouse appear on both tabs.
  // All other members (including other Gen 0 members like siblings/cousins) appear only on their own branch.
  const creator = members.find(m => m.isCreator);
  const creatorSpouseId = creator?.spouseId;

  const filteredMembers = members.filter(m =>
    m.isCreator || m.memberId === creatorSpouseId || m.branch === activeTab
  );

  const layout = buildTreeLayout(filteredMembers);

  // Built from the FULL member list (not filteredMembers) so that strip
  // colours stay consistent for a family group regardless of which tab
  // (paternal/maternal) is currently active.
  const groupColorMap = buildGroupColorMap(members);

  // Always show all 7 generations (Gen -3 to Gen +3), even if empty —
  // each column always ends with a "+" cell so users can add members
  // in any generation without a separate "Add Member" button.
  const generations = [-3, -2, -1, 0, 1, 2, 3];

  return (
    <div style={styles.container}>
      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={activeTab === 'paternal' ? styles.tabActive : styles.tab}
         onClick={() => { setActiveTab('paternal'); onTabChange && onTabChange('paternal'); }}
        >
          Paternal Side
        </button>
        <button
          style={activeTab === 'maternal' ? styles.tabActive : styles.tab}
        onClick={() => { setActiveTab('maternal'); onTabChange && onTabChange('maternal'); }}
        >
          Maternal Side
        </button>
      </div>

      {/* Tree area */}
      <div style={styles.treeArea}>
        {loading ? (
          <div style={styles.empty}>Loading your family tree...</div>
        ) : (
          <div style={styles.grid}>
            {generations.map(gen => (
              <GenerationColumn
                key={gen}
                generation={gen}
                members={layout[gen] || []}
                allMembers={members}
                groupColorMap={groupColorMap}
                creatorEmail={user.email}
                onRightClick={handleRightClick}
                onMemberClick={onMemberClick}
                onAddInColumn={onAddInColumn}
                onDoubleClick={async (member) => {
                  await axios.put(
                    `https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(user.email)}/${member.memberId}`,
                    { isDeceased: !member.isDeceased }
                  );
                  fetchMembers();
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          member={contextMenu.member}
          allMembers={members}
          activeTab={activeTab}
          onAction={handleContextAction}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
});

const styles = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    padding: '16px 24px 0',
    borderBottom: '1px solid #e5e7eb',
    background: '#ffffff',
  },
  tab: {
    padding: '8px 20px',
    border: 'none',
    background: 'transparent',
    color: '#9CA3AF',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
  },
  tabActive: {
    padding: '8px 20px',
    border: 'none',
    background: 'transparent',
    color: '#0e0018',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    borderBottom: '2px solid #D4A017',
    marginBottom: '-1px',
  },
  treeArea: {
    flex: 1,
    overflow: 'auto',
    padding: '32px 24px',
    background: '#f8f7ff',
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '16px',
    minWidth: 'max-content',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '320px',
    color: '#9CA3AF',
    textAlign: 'center',
    gap: '12px',
  },
  emptyIcon:  { fontSize: '48px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: '#4B5563' },
  emptySub:   { fontSize: '13px', maxWidth: '320px', lineHeight: 1.6 },
};

export default FamilyTree;
